<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {ref} from "vue";
import {CapacityFramework, Topic} from "@/types";
import VFieldLabel from "@/components/forms/VFieldLabel.vue";
import {router, useForm} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import NavigationTabWithChip from "@/components/tabs/NavigationTabWithChip.vue";
import NavigationTab from "@/components/tabs/NavigationTab.vue";
import VEditor from "@/components/forms/VEditor.vue";
import {useColors} from "@/composables/colors";

defineOptions({layout: BaseLayout})

const {randomColor} = useColors()

interface Props {
    title: string
    category: 'overview' | 'form'
    capacityFramework: CapacityFramework
    topics: Array<Topic>
}

const props = defineProps<Props>();

const form = useForm({
    name: props.capacityFramework.name,
    description: props.capacityFramework.description,
    topics: props.capacityFramework.topics
})

const search = ref<string>('')
const isBeingEdited = ref<boolean>(false)
const tab = ref<'overview' | 'form'>(props.category);

const toggleEdit = () => {
    isBeingEdited.value = !isBeingEdited.value;
}

const closeEdit = () => {
    isBeingEdited.value = false;
}

const removeTopic = (topic: Topic) => {
    form.topics = form.topics.filter(t => t.id !== topic.id);
}

const goToForm = () => {
    router.get(route('capacity-frameworks.form', { capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id }));
}

const submit = () => {
    form.put(route('capacity-frameworks.update', { capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id }), {
        preserveScroll: true,
    })
}
</script>

<template>
    <v-container>
        <v-row class="align-center justify-space-between">
            <v-col
                cols="12"
                class="pa-0"
            >
                <h1 class="text-h4 font-weight-bold">
                    {{ props.title }} <span class="text-disabled">v{{ props.capacityFramework.version_id }}</span>
                </h1>
            </v-col>
        </v-row>
        <v-row class="mt-5 mb-0">
            <v-col
                cols="12"
                class="pa-0"
            >
                <v-tabs
                    v-model="tab"
                    align-tabs="start"
                    color="primary"
                >
                    <navigation-tab
                        :tab="tab"
                        value="overview"
                    >
                        Overview
                    </navigation-tab>
                    <navigation-tab-with-chip
                        :tab="tab"
                        value="form"
                        :count="props.capacityFramework.capacity_indicators_count"
                        @click="goToForm"
                    >
                        Form
                    </navigation-tab-with-chip>
                </v-tabs>
            </v-col>
        </v-row>
        <v-divider />
        <v-form
            @submit.prevent="submit"
        >
            <v-row class="mt-3">
                <v-col
                    cols="12"
                >
                    <v-field-label>
                        Name (required)
                    </v-field-label>
                    <v-text-field
                        v-model="form.name"
                        type="text"
                        name="name"
                        class="mb-3"
                        placeholder="My Capacity Framework"
                        density="compact"
                        :hide-details="!form.errors.name"
                        :error-messages="form.errors.name"
                        autofocus
                    />
                </v-col>
                <v-col
                    cols="12"
                    md="8"
                >
                    <v-field-label>
                        Framework description
                    </v-field-label>
                    <v-editor
                        v-model="form.description"
                        placeholder="Describe your framework"
                    />
                </v-col>
                <v-col
                    cols="12"
                    md="4"
                >
                    <v-list class="pa-0">
                        <v-list-item>
                            <v-list-item-title class="text-body-2 text-black font-weight-bold">
                                Labels
                                <v-btn
                                    color="black"
                                    variant="text"
                                    size="x-small"
                                    class="float-end py-1 px-2"
                                    @click="toggleEdit"
                                >
                                    <span class="font-weight-regular">
                                        {{ isBeingEdited ? 'Apply' : 'Edit' }}
                                    </span>
                                </v-btn>
                            </v-list-item-title>
                            <v-list-item-subtitle
                                v-if="!isBeingEdited"
                                class="d-block mt-1"
                            >
                                <span
                                    v-if="form.topics.length === 0"
                                    class="text-body-2"
                                >
                                    None
                                </span>
                                <v-chip
                                    v-for="topic in form.topics"
                                    :key="topic.id"
                                    variant="flat"
                                    class="mx-1 mb-1 text-label"
                                    size="x-small"
                                    :color="randomColor(topic.id)"
                                >
                                    {{ topic.name }}
                                    <template #append>
                                        <v-icon-btn
                                            variant="text"
                                            icon="mdi-close"
                                            size="small"
                                            icon-size="small"
                                            class="ml-1"
                                            @click.stop="removeTopic(topic)"
                                        />
                                    </template>
                                </v-chip>
                            </v-list-item-subtitle>
                            <v-list-item-subtitle
                                v-if="isBeingEdited"
                                class="d-flex mt-1"
                            >
                                <v-autocomplete
                                    v-if="isBeingEdited"
                                    v-model="form.topics"
                                    v-model:search="search"
                                    variant="outlined"
                                    :items="props.topics"
                                    density="compact"
                                    clear-on-select
                                    return-object
                                    multiple
                                    hide-details
                                    item-title="name"
                                    item-value="id"
                                    no-data-text="No labels found"
                                    :list-props="{
                                        class: 'py-0'
                                    }"
                                    :menu-props="{
                                        location: 'bottom'
                                    }"
                                    menu-icon="null"
                                    @blur="closeEdit"
                                >
                                    <template #item="{ item, props: binds }">
                                        <v-list-item
                                            :value="binds.value"
                                            link
                                            @click="binds.onClick as Event"
                                        >
                                            <v-list-item-title
                                                class="text-body-2 font-weight-bold"
                                            >
                                                <v-badge
                                                    variant="flat"
                                                    :color="randomColor(item.raw.id)"
                                                    height="8"
                                                    width="6"
                                                    class="mb-3 mx-3"
                                                />
                                                {{ item.raw.name }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </v-autocomplete>
                            </v-list-item-subtitle>
                        </v-list-item>
                    </v-list>
                </v-col>
            </v-row>
            <v-row class="mt-5">
                <v-col
                    cols="12"
                    class="px-0"
                >
                    <v-btn
                        color="primary"
                        type="submit"
                        :disabled="!form.isDirty"
                    >
                        Save changes
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<style scoped>

</style>
